import { useCallback, useEffect, useMemo, useRef } from 'react';

import { InfiniteData, useQueryClient } from '@tanstack/react-query';

import { isValidUuid } from '@utils/validators';

import {
  getAiBuilderMessagesQueryKey,
  getAiBuilderSessionQueryKey,
  useAIBuilderChatSession,
  useAIBuilderMessagesInfinite,
  useAIBuilderNewMessages,
} from 'api/ai-builder/hooks';
import type { PlanTask, TodoItem, ToolData } from 'api/ai-builder/requests';
import {
  ChatSessionResponse,
  MessagesResponse,
  PaginatedMessagesResponse,
} from 'api/ai-builder/requests';

export type ChatMessageRole = 'user' | 'assistant' | 'tool';
export type ToolStatus = 'done' | 'error' | 'pending' | 'accepted' | 'rejected';
type ToolType =
  | 'create_action'
  | 'edit_action'
  | 'delete_action'
  | 'helper'
  | 'move_action'
  | 'create_journey_attribute'
  | 'delete_journey_attribute';

type BaseMessage = {
  id: string;
  role: ChatMessageRole;
  text: string;
  errorType?: string;
  errorMessage?: string;
  messageType?: 'reasoning' | 'standard';
  isCollapsible?: boolean;
  timeTakenSeconds?: number;
  hasSummary?: boolean;
  planSummary?: string;
};

type ToolMessage = Omit<BaseMessage, 'text'> & {
  role: 'tool';
  callId: string;
  toolName?: string;
  toolLabel?: string;
  toolDescription?: string;
  status: ToolStatus;
  text?: string;
  data?: ToolData;
};

type UserMessage = BaseMessage & { role: 'user'; files?: string[] };
type AssistantMessage = BaseMessage & { role: 'assistant' };

export type ChatMessage = UserMessage | AssistantMessage | ToolMessage;

type ToolCallBase = {
  type: 'tool_call';
  callId: string;
  name?: string;
  toolType?: ToolType;
  label?: string;
  description?: string;
  data?: ToolData;
  isHidden?: boolean;
};

type ToolCallStart = ToolCallBase & {
  event: 'start';
};

type ToolCallDelta = ToolCallBase & {
  event: 'delta';
  content?: string;
};

type ToolCallEnd = ToolCallBase & {
  event: 'end';
  hasError?: boolean;
  errorType?: string;
  content?: string;
  todos?: TodoItem[];
};

type ToolCallAllDone = ToolCallBase & {
  event: 'task_complete';
};

type ToolCallEvent = ToolCallStart | ToolCallDelta | ToolCallEnd | ToolCallAllDone;

type StatusBaseEvent = {
  type: 'status';
  event: 'error' | 'interrupted' | 'completed';
};

type ErrorEvent = StatusBaseEvent & {
  event: 'error';
  messageId?: string;
  message?: string;
  errorType?: string;
};

type InterruptedEvent = StatusBaseEvent & {
  event: 'interrupted';
};

type StatusEvent = ErrorEvent | InterruptedEvent;

type AssistantMessageStart = {
  type: 'message';
  event: 'start';
  messageId: string;
  content?: string;
};

type AssistantMessageDelta = {
  type: 'message';
  event: 'delta';
  messageId: string;
  content?: string;
};

type AssistantMessageEvent = AssistantMessageStart | AssistantMessageDelta;

type ReasoningMessageStart = {
  type: 'reasoning_message';
  event: 'start';
  messageId: string;
  content?: string;
  timeTakenSeconds?: number;
};

type ReasoningMessageDelta = {
  type: 'reasoning_message';
  event: 'delta';
  messageId: string;
  content?: string;
  timeTakenSeconds?: number;
};

type ReasoningMessageEvent = ReasoningMessageStart | ReasoningMessageDelta;

export function isReasoningMessageEvent(event: any): event is ReasoningMessageEvent {
  if (typeof event !== 'object' || event === null) return false;
  return 'type' in event && event.type === 'reasoning_message';
}

export function isToolCallEvent(event: any): event is ToolCallEvent {
  if (typeof event !== 'object' || event === null) return false;
  return 'type' in event && event.type === 'tool_call' && 'callId' in event;
}

export function isStatusEvent(event: any): event is StatusEvent {
  if (typeof event !== 'object' || event === null) return false;
  return 'type' in event && event.type === 'status';
}

export function reduceMessagesByWsEvent(
  allMessages: ChatMessage[],
  payload: ToolCallEvent | AssistantMessageEvent | StatusEvent | ReasoningMessageEvent
): ChatMessage[] {
  if (isStatusEvent(payload)) {
    if (payload.event === 'error' && payload.messageId && payload.message) {
      const messageExists = allMessages.some((message) => message.id === payload.messageId);

      if (messageExists) return allMessages;

      const errorMessage: AssistantMessage = {
        id: payload.messageId,
        role: 'assistant',
        text: payload.message,
        errorMessage: payload.message,
        errorType: payload.errorType,
      };

      return [...allMessages, errorMessage];
    }

    return allMessages;
  }

  if (isReasoningMessageEvent(payload)) {
    const messageId = payload.messageId;

    if (payload.event === 'start') {
      const newMessage: ChatMessage = {
        id: messageId,
        role: 'assistant',
        text: payload.content ?? '',
        messageType: 'reasoning',
        isCollapsible: true,
        timeTakenSeconds: payload.timeTakenSeconds,
        planSummary: payload.content ?? '',
      };
      return [...allMessages, newMessage];
    }
  }

  if (isToolCallEvent(payload)) {
    const toolPayload = payload;
    const { event: toolCallEvent, callId: toolCallId, name: toolCallName } = toolPayload;

    const baseToolMessage: ToolMessage = {
      id: toolCallId,
      role: 'tool',
      callId: toolCallId,
      toolName: toolCallName,
      toolLabel: toolPayload.label,
      toolDescription: toolPayload.description,
      status: 'pending',
      data: toolPayload.data,
    };

    if (toolCallEvent === 'start') {
      if (toolPayload.isHidden) return allMessages;

      const toolCallExists = allMessages.some(
        (message) => message.role === 'tool' && message.callId === toolCallId
      );
      if (toolCallExists) return allMessages;

      return [...allMessages, baseToolMessage];
    }

    if (toolCallEvent === 'delta') {
      const { content: toolCallContent } = toolPayload;
      if (!toolCallContent) return allMessages;

      const toolCallIndex = allMessages.findIndex(
        (message) => message.role === 'tool' && message.callId === toolCallId
      );

      if (toolCallIndex === -1) {
        return [...allMessages, baseToolMessage];
      }

      return allMessages;
    }

    if (toolCallEvent === 'end') {
      const { label, description, errorType, todos, isHidden } = toolPayload as ToolCallEnd;

      let updatedMessages = allMessages;
      if (toolCallName === 'todo_write' && todos) {
        const lastPlanIndex = updatedMessages.reduce((lastIdx, message, idx) => {
          if (
            message.role === 'tool' &&
            message.toolName === 'exit_plan_mode' &&
            message.data?.type === 'plan'
          ) {
            return idx;
          }
          return lastIdx;
        }, -1);

        if (lastPlanIndex !== -1) {
          updatedMessages = updatedMessages.map((message, idx) => {
            if (idx === lastPlanIndex && message.role === 'tool') {
              const planData = message.data;
              if (planData?.type === 'plan') {
                return {
                  ...message,
                  data: {
                    ...planData,
                    plan: {
                      ...planData.plan,
                      tasks: planData.plan.tasks.map((task: PlanTask) => {
                        const update = todos.find((todo) => todo.id === task.id);
                        return update ? { ...task, status: update.status } : task;
                      }),
                    },
                  },
                };
              }
            }
            return message;
          });
        }
      }

      if (isHidden) return updatedMessages;

      const endStatus: ToolStatus =
        errorType === 'rejected' ? 'rejected' : errorType === 'accepted' ? 'accepted' : 'done';

      const patch: Partial<ToolMessage> = {
        status: endStatus,
        toolLabel: label,
        toolDescription: description,
      };

      if (toolPayload.data) {
        patch.data = toolPayload.data;
      }

      const toolCallIndex = updatedMessages.findIndex(
        (message) => message.role === 'tool' && message.callId === toolCallId
      );

      return toolCallIndex === -1
        ? [...updatedMessages, { ...baseToolMessage, ...patch }]
        : (updatedMessages.map((message, idx) =>
            idx === toolCallIndex ? { ...message, ...patch } : message
          ) as ChatMessage[]);
    }
  }

  const {
    event: assistantMessageEvent,
    messageId: assistantMessageId,
    content: assistantMessageContent,
  } = payload as AssistantMessageEvent;

  if (assistantMessageEvent === 'start') {
    const messageExists = allMessages.some((message) => message.id === assistantMessageId);

    if (messageExists) return allMessages;

    const initialText = assistantMessageContent ?? '';
    const newMessage: ChatMessage = {
      id: assistantMessageId,
      role: 'assistant',
      text: initialText,
    };
    return [...allMessages, newMessage];
  }

  if (assistantMessageEvent === 'delta') {
    if (!assistantMessageId || !assistantMessageContent) {
      return allMessages;
    }

    return allMessages.map((message) =>
      message.id === assistantMessageId && message.role === 'assistant'
        ? { ...message, text: `${message.text}${assistantMessageContent}` }
        : message
    );
  }

  return allMessages;
}

type UseAIBuilderChatIdOptions = {
  journeyShortId: string;
};

export const useAIBuilderChat = ({ journeyShortId }: UseAIBuilderChatIdOptions) => {
  const queryClient = useQueryClient();
  const isFetchingNewerRef = useRef(false);

  const { data: sessionData, isLoading: isSessionLoading } =
    useAIBuilderChatSession(journeyShortId);
  const shortId = sessionData?.shortId;
  const messagesEnabled = Boolean(shortId) && sessionData?.hasMessages !== false;
  const {
    data: pagesData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isMessagesLoading,
  } = useAIBuilderMessagesInfinite(journeyShortId, { enabled: messagesEnabled });
  const newMessagesMutation = useAIBuilderNewMessages();

  const messages = useMemo(() => {
    if (!pagesData?.pages) return [];
    const allPages = [...pagesData.pages].reverse();
    return allPages.flatMap((page) => {
      return page.messages;
    }) as ChatMessage[];
  }, [pagesData]);

  const session = sessionData;
  const mode = session?.mode;
  const sessionReady = Boolean(session);
  const messagesReady = session?.hasMessages === false || Boolean(pagesData?.pages?.length);
  const isInitialLoading =
    isSessionLoading || (messagesEnabled && isMessagesLoading) || !sessionReady || !messagesReady;

  const getLatestServerMessageId = useCallback((items: ChatMessage[]) => {
    const latest = items.findLast((item) => {
      return Boolean(item?.id && !isValidUuid(item.id));
    });
    return latest?.id ?? null;
  }, []);

  const updateSessionState = useCallback(
    (response: ChatSessionResponse) => {
      queryClient.setQueryData(
        getAiBuilderSessionQueryKey(journeyShortId),
        (old: ChatSessionResponse | undefined) => ({
          ...old,
          shortId: response.shortId,
          mode: response.mode,
          isGenerating: response.isGenerating,
        })
      );
    },
    [queryClient, journeyShortId]
  );

  const mergeNewMessages = useCallback(
    (response: MessagesResponse | PaginatedMessagesResponse) => {
      updateSessionState(response);
      queryClient.setQueryData(
        getAiBuilderMessagesQueryKey(journeyShortId),
        (old: InfiniteData<PaginatedMessagesResponse> | undefined) => {
          if (!old || !old.pages.length) return old;

          const existingIds = new Set(
            old.pages.flatMap((page) => page.messages.map((message) => message.id))
          );

          const newMessages = response.messages.filter((message) => !existingIds.has(message.id));
          const firstPage = old.pages[0];
          const hasPagination = 'hasMore' in response;
          const paginationChanged = hasPagination
            ? firstPage.hasMore !== response.hasMore ||
              firstPage.oldestMessageId !== response.oldestMessageId
            : false;

          if (!newMessages.length && !paginationChanged) {
            return old;
          }

          return {
            ...old,
            pages: old.pages.map((page, index) => {
              if (index === 0) {
                return {
                  ...page,
                  messages: [...page.messages, ...newMessages],
                  ...(hasPagination
                    ? {
                        hasMore: response.hasMore,
                        oldestMessageId: response.oldestMessageId,
                      }
                    : {}),
                };
              }
              return page;
            }),
          };
        }
      );
    },
    [queryClient, journeyShortId, updateSessionState]
  );

  const fetchNewMessagesOnFocus = useCallback(async () => {
    if (!shortId || isFetchingNewerRef.current) return;
    const latestServerMessageId = getLatestServerMessageId(messages);
    if (!latestServerMessageId) return;

    isFetchingNewerRef.current = true;
    try {
      const response = await newMessagesMutation.mutateAsync({
        journeyShortId,
        afterId: latestServerMessageId,
      });
      mergeNewMessages(response);
    } finally {
      isFetchingNewerRef.current = false;
    }
  }, [
    shortId,
    journeyShortId,
    messages,
    getLatestServerMessageId,
    mergeNewMessages,
    newMessagesMutation,
  ]);

  useEffect(() => {
    const handleFocus = () => {
      void fetchNewMessagesOnFocus();
    };
    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [fetchNewMessagesOnFocus]);

  const clearChat = useCallback(() => {
    queryClient.removeQueries(getAiBuilderMessagesQueryKey(journeyShortId));
    queryClient.removeQueries(getAiBuilderSessionQueryKey(journeyShortId));
  }, [queryClient, journeyShortId]);

  const startRequest = useCallback(() => {
    if (!shortId || !mode) return;
    queryClient.setQueryData(
      getAiBuilderSessionQueryKey(journeyShortId),
      (old: ChatSessionResponse | undefined) => {
        if (!old) {
          return {
            shortId,
            mode,
            isGenerating: true,
          };
        }
        return {
          ...old,
          isGenerating: true,
        };
      }
    );
  }, [queryClient, journeyShortId, shortId, mode]);

  const endRequest = useCallback(() => {
    if (!shortId || !mode) return;
    queryClient.setQueryData(
      getAiBuilderSessionQueryKey(journeyShortId),
      (old: ChatSessionResponse | undefined) => {
        if (!old) {
          return {
            shortId,
            mode,
            isGenerating: false,
          };
        }
        return {
          ...old,
          isGenerating: false,
        };
      }
    );
  }, [queryClient, journeyShortId, shortId, mode]);

  return {
    shortId,
    messages,
    session,
    sessionReady,
    messagesReady,
    isInitialLoading,
    clearChat,
    startRequest,
    endRequest,
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
  };
};
